import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { downgradeComponent } from '@angular/upgrade/static';
import App from '@app/app';
import { ApplePayCheckoutService } from './apple-pay-checkout.service';
import { ApplePayAddressService } from './apple-pay-address.service';
import { ApplePayEmailValidationService } from './apple-pay-email-validation.service';
import { ApplePayTelemetryService } from './apple-pay-telemetry.service';
import { StudentDetailService } from '@app/service/student-detail/student-detail.service';
import spinnerSrc from 'images/new_spinner.svg';

@Component({
  selector: 'lt-apple-pay-button',
  templateUrl: './apple-pay-button.component.html',
  styleUrl: './apple-pay-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [ApplePayCheckoutService, ApplePayAddressService, ApplePayEmailValidationService, ApplePayTelemetryService]
})
export class ApplePayButtonComponent implements OnInit {
  private readonly checkoutService = inject(ApplePayCheckoutService);
  private readonly studentDetailService = inject(StudentDetailService);

  public express = input<boolean>(false);
  public preCheckout = output<void>();
  public preCheckoutValidation = input<(() => boolean) | undefined>(undefined);

  private isInitialized = signal<boolean>(false);
  public isSupported = signal<boolean>(false);
  // Not ready (shows the spinner) while an express cart-edit tax recompute is in flight,
  // so the sheet can never be opened with a stale total.
  public isReady = computed(() => this.isInitialized() && !this.checkoutService.isRecomputingTax());

  ngOnInit(): void {
    this.isSupported.set(this.checkoutService.isSupported);
    void this.checkoutService
      .initialize(this.express())
      .then(() => this.setInitialized(true))
      .catch((e) => {
        console.error('failed to init apple pay', e);
        this.setInitialized(false);
      });
  }

  /**
   * Sets isInitialized on a fresh microtask so the write is never nested
   * inside another component's change-detection pass (avoids NG0600).
   */
  private setInitialized(value: boolean): void {
    queueMicrotask(() => this.isInitialized.set(value));
  }

  get spinner(): string {
    return spinnerSrc;
  }

  startPayment(): void {
    if (this.express() && this.studentDetailService.shouldOpenStudentDetailModal()) {
      this.studentDetailService.openStudentDetailModalWithCallback(() => this.continueCheckout());
      return;
    }

    this.continueCheckout();
  }

  private continueCheckout(): void {
    this.preCheckout.emit();

    const validationFn = this.preCheckoutValidation();
    if (validationFn && !validationFn()) {
      return;
    }

    this.checkoutService.startSession(this.express());
  }
}

App.directive(
  'ltApplePayButton',
  downgradeComponent({
    component: ApplePayButtonComponent
  })
);
